import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { UnsavedWorkService } from '@shared/services/unsaved-work.service';
import { User } from '@features/users/models/user';
import { GeneralResponse } from '@shared/enums/general-response.enum';
import { AccountVerificationType } from '@features/auth/enums/account-verification-type.enum';
import { JWTTokenValidation } from '@core/enums/JWT-token-validation.enum';
import { UserDeviceService } from './user-device.service';
import { JwtDecoderService } from './jwt-decoder.service';
import { filterNullEntity } from '@shared/utils/filter-null-entity.util';
import { ApiConstant } from '@shared/constants/api.constant';
import { ApiBaseService } from '@core/services/api-base.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SharedConstants } from '@shared/model/shared-constants';
import { Mapper } from './mapper';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { ResetPasswordMethods } from '@features/auth/enums/reset-password-methods.enum';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { UserDevice } from '@features/users/models/user-device';
import { RoutesUtil } from '@shared-features/utils/routes.util';
import { StorageConstant } from '@shared/constants';
import { LocalStorageUtil } from '@shared/utils/local-storage.util';
import { Router } from '@angular/router';
import { PasswordResetType } from '@shared/enums/password-reset-type.enum';
import { RecaptchaVersion } from '@shared/enums/recaptcha-version.enum';
import { AsyncFeedbackService } from '@shared/services/async-feedback.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { OtpResponse } from '@features/auth/models/otp.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUser$: BehaviorSubject<User> = new BehaviorSubject(null);
  public redirectionURL = '';

  constructor(
    private baseAPI: ApiBaseService,
    private mapper: Mapper,
    private userDeviceService: UserDeviceService,
    private jwtDecoderService: JwtDecoderService,
    private refreshTokenHandler: HttpBackend,
    private featureFlagService: FeatureFlagService,
    private router: Router,
    protected asyncFeedbackService: AsyncFeedbackService,
    private translateService: TranslateService,
    private matDialog: MatDialog,
    private unsavedWorkService: UnsavedWorkService,
  ) {
    // Restore the current user whenever a token exists (Valid OR Expired).
    // An expired access token is still decodable and TokenInterceptor will
    // refresh it on the first request; populating roles here prevents route
    // guards from wrongly redirecting to access-denied on a fresh tab.
    if (this.jwtDecoderService.isThereValidToken() !== JWTTokenValidation.NotFound) {
      this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
    }
  }

  isLoggedIn(): boolean {
    return this.jwtDecoderService.isThereValidToken() !== JWTTokenValidation.NotFound;
  }

  registerDevice(notificationToken: string): Observable<UserDevice> {
    let userDevice = this.userDeviceService.getCurrentUserDevice();
    if (!userDevice || !notificationToken) {
      userDevice = this.userDeviceService.generateNewDevice();
    }
    const timezoneOffset = -1 * new Date().getTimezoneOffset();
    const body = filterNullEntity({ ...userDevice, timezone: timezoneOffset });

    return this.baseAPI.post(ApiConstant.REGISTER_DEVICE, body).pipe(
      map((res) => {
        this.userDeviceService.saveUserDevice(userDevice);
        return userDevice;
      })
    );
  }

  login(email: string, password: string, otp?: string): Observable<User> {
    const userDevice = this.userDeviceService.getCurrentUserDevice();
    const body = filterNullEntity({
      username: email,
      password,
      deviceId: userDevice?.deviceId,
      platform: userDevice?.osType || 'WEB',
      app: SharedConstants.LOGIN_APP,
      otp,
    });
    return this.baseAPI.post(ApiConstant.LOGIN, body).pipe(
      map((res) => this.mapper.fromJson(User, res.data)),
      switchMap((user) => this.featureFlagService.init(user)),
      tap((user) => this.saveTokenAndUpdateUser(user))
    );
  }

  logout(redirectToReset = false, showMessage = true): Observable<string> {
    this.matDialog.closeAll();

    // Unsaved work is persisted BEFORE the logout call, which can invalidate the session server-side.
    // Skipped when `redirectToReset`, which is only ever the 428 password-expired path: every further
    const saveUnsavedWork$ =
      (redirectToReset || !this.isLoggedIn()) ? of(null) : this.unsavedWorkService.saveAll();

    return saveUnsavedWork$.pipe(
      switchMap(() => this.logoutRequest()),
      tap((res) => {
        this.resetUser();
        const targetRoute = redirectToReset
          ? [RoutesUtil.AuthResetPasswordBy.url()]
          : [RoutesUtil.AuthLogin.url()];

        const queryParams = redirectToReset
          ? { queryParams: { resetType: PasswordResetType.Reset } }
          : {};

        if (showMessage) {
          this.asyncFeedbackService.showSuccessMessage(res);
        }
        this.router.navigate(targetRoute, queryParams);
      }),
      catchError(() => {
        const failureMsg = this.translateService.instant('core.FailedToLogout');
        this.asyncFeedbackService.showFailureMessage(failureMsg);
        return of(failureMsg);
      })
    );
  }

  logoutRequest(): Observable<string> {
    return this.baseAPI.post(ApiConstant.LOGOUT, {}).pipe(map((res) => res.data));
  }

  resetUser(): void {
    this.jwtDecoderService.removeCurrentToken();
    LocalStorageUtil.removeItem(SharedConstants.RENTAL_PROFIT_FAV);
    LocalStorageUtil.removeItem(StorageConstant.COLUMN_OPTIONS_KEY);
    this.currentUser$.next(null);
  }

  refreshTheToken(): Observable<User> {
    const userDevice = this.userDeviceService.getCurrentUserDevice();
    const currentUser = this.jwtDecoderService.getCurrentUserFromJWTToken();
    /**
     * @Note:
     * Wrokaround to override the JwtHttpInterceptor for refreshing the token.
     */
    const http = new HttpClient(this.refreshTokenHandler);
    // `UDK` can be lost while the tokens survive (a version bump clears localStorage), and the
    // backend then rejects the refresh with "Device id is required". The token carries the same
    // deviceId, so fall back to it rather than sending nothing.
    const deviceId =
      userDevice?.deviceId || this.jwtDecoderService.getDeviceIdFromToken(currentUser?.accessToken);
    const body = {
      deviceId,
      platform: userDevice?.osType ,
      app: SharedConstants.LOGIN_APP,
    };
    const options = {
      headers: this.baseAPI
        .getHeaders()
        .append('Authorization', 'Bearer ' + currentUser?.refreshToken),
    };
    return http.post(this.baseAPI.switchUrlBase(ApiConstant.REFRESH_TOKEN), body, options).pipe(
      map((res: any) => this.mapper.fromJson(User, res.data)),
      tap((user) => this.saveTokenAndUpdateUser(user))
    );
  }

  verifyEmail(
    token: string,
    password: string,
    verificationType: AccountVerificationType
  ): Observable<GeneralResponse> {
    const body = { token, verificationType, password };
    return this.baseAPI.post(ApiConstant.VERIFY_EMAIL, body).pipe(
      map((res) => {
        return res.data;
      })
    );
  }

  forgetPassword(
    type: ResetPasswordMethods,
    value: string,
    recaptchaToken: string,
    recaptchaVersion: RecaptchaVersion = RecaptchaVersion.V3
  ): Observable<OtpResponse> {
    const idKeyName = type === ResetPasswordMethods.EMAIL ? 'email' : 'mobileNumber';
    const body = { type, [idKeyName]: value };
    const headers = this.getRecaptchaHeaders(recaptchaToken, recaptchaVersion);
    const options = { headers: headers };

    return this.baseAPI.post(ApiConstant.FORGET_PASSWORD, body, options).pipe(
      map((res) => this.mapper.fromJson(OtpResponse, res.data))
    );
  }

  forgetPasswordVerifyMobile(otp: string, mobileNumber: string): Observable<string> {
    const body = { otp, mobileNumber };
    return this.baseAPI.post(ApiConstant.FORGET_PASSWORD_VERIFY_MOBILE, body).pipe(
      map((res) => {
        return res.data;
      })
    );
  }
  requireOTP(
    payload: {
      mobileNumber: string;
      password: string;
      isLogin: boolean;
    },
    recaptchaToken: string,
    deviceId: string,
    recaptchaVersion: RecaptchaVersion = RecaptchaVersion.V3
  ): Observable<OtpResponse> {
    let headers = this.getRecaptchaHeaders(recaptchaToken, recaptchaVersion);
    headers = headers.append('device-id', deviceId);
    const options = { headers: headers };
    return this.baseAPI.post(ApiConstant.REQUEST_OTP, payload, options).pipe(
      map((res) => this.mapper.fromJson(OtpResponse, res.data))
    );
  }

  private getRecaptchaHeaders(recaptchaToken: string, recaptchaVersion: RecaptchaVersion): HttpHeaders {
    const headerName = recaptchaVersion === RecaptchaVersion.V2 ? 'recaptcha-v2' : 'recaptcha';
    return this.baseAPI.getHeaders().append(headerName, recaptchaToken);
  }

  private saveTokenAndUpdateUser(user: User): void {
    this.jwtDecoderService.saveToken(user.toJson());
    this.currentUser$.next(this.jwtDecoderService.getCurrentUserFromJWTToken());
  }
}
